// src/pages/About.js
import React from 'react';
import { FaHandshake, FaCogs, FaRecycle, FaUserTie } from 'react-icons/fa';
import { useSettings } from '../context/SettingsContext';
import AnimatedBackground from '../components/AnimatedBackground';

export default function About() {
  const { settings } = useSettings();
  
  return (
    <div className="about-page">
      {/* ───── DYNAMIC HERO SECTION ───── */}
      <section
        className="about-hero"
        style={{
          position: 'relative',
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          marginRight: 'calc(50% - 50vw)',
          minHeight: '600px',
          height: '600px',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        {/* Dynamic Background */}
        {settings.aboutHero.backgroundType === 'video' && settings.aboutHero.backgroundMedia && (
          <video
            key={settings.aboutHero.backgroundMedia}
            className="hero-video"
            autoPlay
            loop
            muted
            playsInline
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 1,
            }}
          >
            <source src={`${settings.aboutHero.backgroundMedia}?t=${Date.now()}`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        
        {settings.aboutHero.backgroundType === 'image' && settings.aboutHero.backgroundMedia && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${settings.aboutHero.backgroundMedia})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: 1,
            }}
          />
        )}
        
        {settings.aboutHero.backgroundType === 'animation' && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1,
            }}
          >
            <AnimatedBackground />
          </div>
        )}

        {/* Dynamic Overlay */}
        {settings.aboutHero.overlayOpacity > 0 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: `${settings.aboutHero.overlayColor}${Math.round(settings.aboutHero.overlayOpacity * 255).toString(16).padStart(2, '0')}`,
              zIndex: 2,
            }}
          />
        )}
        
        <div
          style={{
            position: 'absolute',
            zIndex: 3,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '0 1rem',
          }}
        >
          <h1 style={{
            fontSize: '3rem',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #007BFF 0%, #0056b3 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold',
            filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))'
          }}>
            {settings.aboutHero.title}
          </h1>
          <p style={{
            fontSize: '1.2rem',
            maxWidth: 800,
            margin: '0 auto',
            color: '#FFFFFF',
            textShadow: '2px 2px 6px rgba(0, 0, 0, 0.9)',
            fontWeight: '500'
          }}>
            {settings.aboutHero.subtitle}
          </p>
        </div>
      </section>

      {/* ───── DESCRIPTION ───── */}
      <section
        style={{
          maxWidth: 900,
          margin: '0 auto',
          padding: '2rem 1rem',
          textAlign: 'center',
          lineHeight: 1.6,
          color: '#6c757d',
        }}
      >
        <p style={{ marginBottom: '1.5rem', color: '#6c757d' }}>
          At Kentech Phone Repairs, we specialize in premium repair services for smartphones, laptops, gaming consoles, and more. Our mission is simple: to provide fast, reliable, and professional repairs that restore your devices to their optimal performance.
        </p>
        <p style={{ marginBottom: '1.5rem', color: '#6c757d' }}>
          We understand how important your devices are in your daily life, which is why our experienced technicians use only high-quality or original parts, handling every repair with precision and care. Whether it's a screen replacement, battery swap, or complex motherboard repair, we ensure your device is treated as if it were our own.
        </p>
        <p style={{ color: '#6c757d' }}>
          With convenient services including in-lab repairs, pick-up/drop off at our trading store Gadgets Xperts at 24-25 New Rd, Gravesend DA11 0AA, and mail-in options, we make the repair process seamless and stress-free. At Kentech Phone Repairs, trust, quality, and customer satisfaction are at the heart of everything we do.
        </p>
      </section>

      {/* ───── WHY CHOOSE US ───── */}
      <section
        style={{
          maxWidth: 1000,
          margin: '0 auto',
          padding: '2rem 1rem',
          textAlign: 'center',
        }}
      >
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#6c757d' }}>
          Why Choose Us?
        </h2>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2rem',
            justifyContent: 'center',
            marginTop: '1.5rem',
          }}
        >
          {/* No Fix - No Fee */}
          <ValueCard
            icon={<FaHandshake size={48} style={{ color: '#007BFF' }} />}
            title='"No Fix, No Fee" Promise'
            text="You only pay for results."
          />

          {/* Best Quality Parts */}
          <ValueCard
            icon={<FaCogs size={48} style={{ color: '#007BFF' }} />}
            title="Best Quality Parts"
            text="Premium and original parts used for every repair without compromise"
          />

          {/* Right to Repair */}
          <ValueCard
            icon={<FaRecycle size={48} style={{ color: '#007BFF' }} />}
            title="Right to Repair"
            text="Fighting for fair, sustainable tech repair."
          />

          {/* 20+ Years of Expertise */}
          <ValueCard
            icon={<FaUserTie size={48} style={{ color: '#007BFF' }} />}
            title="20+ Years Expertise"
            text="Veteran technicians mentoring the next generation."
          />
        </div>
      </section>
    </div>
  );
}

/* ─────────── helper ─────────── */
function ValueCard({ icon, title, text }) {
  return (
    <div
      style={{
        flex: '1 1 220px',
        maxWidth: 240,
        padding: '1rem',
        textAlign: 'center',
        border: '1px solid #ddd',
        borderRadius: 8,
      }}
    >
      {icon}
      <h3 style={{ marginTop: '.75rem', fontSize: '1.1rem', color: '#6c757d' }}>
        {title}
      </h3>
      <p style={{ fontSize: '.95rem', color: '#6c757d' }}>{text}</p>
    </div>
  );
}
